import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Record a voice message.
 *
 * **A recording, not a transcript.** Speech recognition would hand back text
 * with no audio to fall back on when it mishears a centre name, a tenant name
 * or a street — precisely the vocabulary it is worst at, and precisely what a
 * site visit is about. The recording is the artefact; transcription is a later
 * enrichment on top of it.
 *
 * Two platform facts shape this hook:
 *
 *  - **Safari emits `audio/mp4`, not the `audio/webm` most examples assume.**
 *    The container is feature-detected rather than hardcoded, and a browser
 *    supporting neither reports `unsupported` so the button can be hidden
 *    instead of failing on tap.
 *  - **`getUserMedia` needs a secure context.** Over plain HTTP iOS refuses
 *    silently — no prompt, no callback — which reads as a broken app rather
 *    than a missing prerequisite, so it is reported explicitly.
 */

export type RecorderStatus =
	| 'idle'
	| 'requesting'
	| 'recording'
	| 'denied'
	| 'unsupported'
	| 'insecure'
	| 'error'

export interface UseAudioRecorderOptions {
	/**
	 * Hard stop. Two minutes: long enough for a considered note, short enough
	 * that an accidental press does not put a 40MB upload on someone's LTE.
	 */
	maxDurationMs?: number
	/** Called once with the finished recording. Not called on cancel. */
	onComplete?: (file: File) => void
}

export interface UseAudioRecorderResult {
	status: RecorderStatus
	/** Whole seconds elapsed, for the UI counter. */
	seconds: number
	/** False when this browser cannot record at all — hide the affordance. */
	supported: boolean
	error: string | null
	start: () => void
	/** Finish and hand the file to `onComplete`. */
	stop: () => void
	/** Throw the recording away. */
	cancel: () => void
}

/**
 * Containers worth trying, best first.
 *
 * Safari only offers mp4; Chrome and Firefox prefer webm/opus, which is both
 * smaller and better at speech. Ordering mp4 first would hand Chrome a worse
 * file than it can make, so opus leads and mp4 catches Safari.
 */
const CANDIDATE_TYPES = [
	'audio/webm;codecs=opus',
	'audio/ogg;codecs=opus',
	'audio/webm',
	'audio/mp4',
]

const EXTENSIONS: Record<string, string> = {
	'audio/webm': 'webm',
	'audio/ogg': 'ogg',
	'audio/mp4': 'm4a',
	'audio/mpeg': 'mp3',
	'audio/wav': 'wav',
}

/** '' when nothing matches — the browser then picks for itself. */
export const pickAudioMimeType = (): string => {
	if (typeof MediaRecorder === 'undefined') return ''
	if (typeof MediaRecorder.isTypeSupported !== 'function') return ''
	return CANDIDATE_TYPES.find(type => MediaRecorder.isTypeSupported(type)) ?? ''
}

/** A filename the recipient can make sense of once downloaded. */
export const audioFileName = (mimeType: string, at: Date = new Date()): string => {
	const base = (mimeType || '').split(';')[0]
	const extension = EXTENSIONS[base] || 'm4a'
	const stamp = at.toISOString().slice(0, 19).replace(/[:T]/g, '-')
	return `voice-${stamp}.${extension}`
}

const MESSAGES: Record<Exclude<RecorderStatus, 'idle' | 'requesting' | 'recording'>, string> = {
	denied:
		'Microphone access is off for this site. iOS will not ask again, so it has to be re-enabled in Settings.',
	unsupported: 'This browser cannot record audio.',
	insecure: 'Recording needs a secure connection. Open the app over HTTPS rather than a plain address.',
	error: 'Something went wrong while recording.',
}

const useAudioRecorder = ({
	maxDurationMs = 120_000,
	onComplete,
}: UseAudioRecorderOptions = {}): UseAudioRecorderResult => {
	const [status, setStatus] = useState<RecorderStatus>('idle')
	const [seconds, setSeconds] = useState(0)

	const recorderRef = useRef<MediaRecorder | null>(null)
	const streamRef = useRef<MediaStream | null>(null)
	const chunksRef = useRef<Blob[]>([])
	const cancelledRef = useRef(false)
	const tickRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const capRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const onCompleteRef = useRef(onComplete)
	onCompleteRef.current = onComplete

	const supported =
		typeof MediaRecorder !== 'undefined' &&
		typeof navigator !== 'undefined' &&
		Boolean(navigator.mediaDevices?.getUserMedia)

	/**
	 * Release the microphone.
	 *
	 * Not housekeeping: while a track is live iOS keeps the orange recording
	 * indicator lit, and a user who sees that after sending a message is right
	 * to distrust the app.
	 */
	const teardown = useCallback(() => {
		if (tickRef.current) clearInterval(tickRef.current)
		if (capRef.current) clearTimeout(capRef.current)
		tickRef.current = null
		capRef.current = null
		streamRef.current?.getTracks().forEach(track => track.stop())
		streamRef.current = null
		recorderRef.current = null
	}, [])

	useEffect(() => teardown, [teardown])

	const start = useCallback(async () => {
		if (!supported) {
			setStatus('unsupported')
			return
		}
		if (typeof window !== 'undefined' && window.isSecureContext === false) {
			setStatus('insecure')
			return
		}
		if (recorderRef.current) return

		setStatus('requesting')
		cancelledRef.current = false
		chunksRef.current = []
		setSeconds(0)

		let stream: MediaStream
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true })
		} catch (err: any) {
			// NotAllowedError covers both a refusal and a policy block; either way
			// the user has to go to Settings, because iOS will not re-prompt.
			setStatus(err?.name === 'NotAllowedError' ? 'denied' : 'error')
			return
		}

		streamRef.current = stream

		try {
			const mimeType = pickAudioMimeType()
			const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
			recorderRef.current = recorder

			recorder.ondataavailable = event => {
				if (event.data && event.data.size > 0) chunksRef.current.push(event.data)
			}

			recorder.onstop = () => {
				const type = recorder.mimeType || mimeType || 'audio/mp4'
				const chunks = chunksRef.current
				chunksRef.current = []
				teardown()
				setStatus('idle')

				if (cancelledRef.current) return
				const blob = new Blob(chunks, { type })
				// A zero-length recording is a tap, not a message. Sending it would
				// put an unplayable bubble in the thread.
				if (!blob.size) return
				onCompleteRef.current?.(new File([blob], audioFileName(type), { type }))
			}

			recorder.start()
			setStatus('recording')

			tickRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
			// Stops itself rather than relying on the user: an accidental press
			// should not become a forty-megabyte upload.
			capRef.current = setTimeout(() => {
				if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
			}, maxDurationMs)
		} catch {
			teardown()
			setStatus('error')
		}
	}, [supported, maxDurationMs, teardown])

	const stop = useCallback(() => {
		cancelledRef.current = false
		if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
	}, [])

	const cancel = useCallback(() => {
		cancelledRef.current = true
		if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
		else {
			teardown()
			setStatus('idle')
		}
	}, [teardown])

	return {
		status,
		seconds,
		supported,
		error: status in MESSAGES ? MESSAGES[status as keyof typeof MESSAGES] : null,
		start: () => {
			void start()
		},
		stop,
		cancel,
	}
}

export default useAudioRecorder
